import { SelectionChangedEvent } from "ag-grid-community";
import { ChargeWaiverEntity } from "domain/entity/ChargeWaiver/ChargeWaiverEntity";
import { INITIAL_CHARGE_WAIVER_COL_DEF, transferRowData } from "presentation/constant/ChargeWaiver/ChargeWaiverColumnDefinition";
import { ChargeWaiverConstant } from "presentation/constant/ChargeWaiver/ChargeWaiverConstant";
import { useChargeWaiverVM } from "presentation/hook/ChargeWaiver/useChargeWaiverVM";
import { useANAInfoTracked } from "presentation/store/ANAInfo";
import { useChargeWaiverTracked } from "presentation/store/ChargeWaiver/ChargeWaiverProvider";
import NbisTable from "presentation/view/components/TableWrapper/NbisTable";
import { TableWrapper } from "presentation/view/components/TableWrapper/TableWrapper";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Core } from "veronica-ui-component";
import { GroupButtons, Loader } from "veronica-ui-component/dist/component/core";


const ChargeWaiverTablePanel: React.FC = () => {
    const [chargeWaiverState] = useChargeWaiverTracked();
    const chargeWaiverVM = useChargeWaiverVM();
    const gridRef: any = useRef(null);
    const { chargeWaiverList, isApprovalCount, searchCriteria, isLoading, isApproval, isSubmit, isSubmitCount } = chargeWaiverState;
    const CHARGE_WAIVER_CONSTANT = ChargeWaiverConstant.Title;
    const [onTableSelectionClicked, setOnTableSelectionClicked] = useState<boolean>(false);
    const [anaInfoState] = useANAInfoTracked();
    const { operatingUnit } = anaInfoState;
    const [showOverlay, setShowOverlay] = useState<boolean>(false);

    useEffect(() => {
        setShowOverlay(chargeWaiverState.isSearchLoading || (!chargeWaiverState.isSearchLoading &&
            chargeWaiverList && chargeWaiverList.length) ? false : true);

        if (gridRef.current?.gridRef.current && !chargeWaiverState.isSearchLoading &&
            gridRef.current?.gridRef.current?.api?.getSelectedNodes()?.length > 0) {
            gridRef.current?.gridRef.current?.api?.getSelectedNodes()?.forEach((node: any) => {
                node.setSelected(false);
            });
        }
    }, [chargeWaiverState.isSearchLoading, chargeWaiverList]);

    useEffect(() => {
        // const columnDefs = (chargeWaiverState.dynamicTableColumns?.slice());

        if (!chargeWaiverState.selectedRows ||
            chargeWaiverState.selectedRows.length <= 0) {
            //gridRef.current?.gridRef.current.api?.setColumnDefs(columnDefs);
            if (!onTableSelectionClicked) {
                gridRef.current?.gridRef.current.api?.deselectAll();
            }
        }
    })

    useEffect(() => {
        if (!onTableSelectionClicked) return;

        gridRef.current?.gridRef.current.api?.deselectAll();
    }, [onTableSelectionClicked])

    const handleSelectionChange = useCallback((e: SelectionChangedEvent) => {
        setOnTableSelectionClicked(true);
        const selectedRows = e.api.getSelectedRows();
        chargeWaiverVM.updateSelectedRows(selectedRows);
    }, [chargeWaiverVM])

    const handleRowDoubleClick = useCallback((waiver: ChargeWaiverEntity) => {
        chargeWaiverVM.onRowDoubleClick(waiver);
    }, [chargeWaiverVM])

    const handleGroupButtonClick = useCallback(async (buttonId: string) => {
        chargeWaiverVM.onShowLoading();
        await chargeWaiverVM.onGroupButtonClick(buttonId, searchCriteria).then((data) => {
            chargeWaiverVM.onHideLoading();
        }).catch(error => {
            chargeWaiverVM.onHideLoading();
        }).finally(() => {
            chargeWaiverVM.onHideLoading();
        })
    }, [chargeWaiverVM, searchCriteria]);

    const template = useMemo(() => {
        return (
            <>
                <Core.CheckButton
                    checked={isApproval}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => { handleGroupButtonClick(e.target.id) }}
                    showBadge={true}
                    badgeValue={isApprovalCount > 999 ? '999+' : isApprovalCount}
                    disabled={isApprovalCount === 0}
                    size="medium"
                    inputId="isApproval"
                    label={CHARGE_WAIVER_CONSTANT.PENDING_APPROVAL}
                />
                <Core.CheckButton
                    checked={isSubmit}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => { handleGroupButtonClick(e.target.id) }}
                    showBadge={true}
                    badgeValue={isSubmitCount > 999 ? '999+' : isSubmitCount}
                    disabled={isSubmitCount === 0}
                    size="medium"
                    inputId="isSubmit"
                    label={CHARGE_WAIVER_CONSTANT.PENDING_SUBMIT}
                />
            </>
        )
    }, [CHARGE_WAIVER_CONSTANT.PENDING_APPROVAL, CHARGE_WAIVER_CONSTANT.PENDING_SUBMIT, handleGroupButtonClick, isApprovalCount, isApproval, isSubmit, isSubmitCount])

    const memoChargeWaiverTable = useMemo(() => {

        return (
            <NbisTable
                id='charge-waiver-table'
                isNewColumnSetting={true}
                columns={(operatingUnit === 'SAPT' || operatingUnit === 'KICT')
                    ? INITIAL_CHARGE_WAIVER_COL_DEF.filter(col => col.field !== 'waiverType' && col.field !== 'tierNo' && col.field !== 'tierFromDate' && col.field !== 'tierToDate'
                    ) : INITIAL_CHARGE_WAIVER_COL_DEF}
                data={transferRowData(chargeWaiverList ?? [])}
                showPaginator={false}
                editable={false}
                showAddIcon={false}
                showDeleteButton={false}
                showReloadIcon={false}
                isScrollHighlighted={true}
                selectionMode={false}
                rowSelection={"multiple"}
                isRowHighligted={true}
                handleSelectionChanged={handleSelectionChange}
                onRowDoubleClick={(e: any, waiver: ChargeWaiverEntity) => handleRowDoubleClick(waiver)}
                gridHeight="customHeight"
                customHeight="calc(100vh - 125px)"
                ref={gridRef}
                headerLabel={
                    <GroupButtons
                        alignment="single-selection"
                        errorMessage=""
                        label=""
                        children={template}
                    />
                }
                showDefaultOverlayNoRowsTemplate={showOverlay}
            />
        );
    }, [operatingUnit, chargeWaiverList, handleSelectionChange, template, handleRowDoubleClick, showOverlay])

    return <><TableWrapper>
        {isLoading && <Loader Indicator="Spinner" size="Medium" />}
        {memoChargeWaiverTable}
    </TableWrapper></>;
}

export default memo(ChargeWaiverTablePanel);
